import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { decodeProductId, encodeProductId } from '@/lib/product-url-codec';
import { SectionReviewScreen } from '@/features/tech-spec-extraction/section-review/SectionReviewScreen';
import { SupportingDocumentsUploader } from '@/features/product/components/SupportingDocumentsUploader';

/**
 * Review route — where an uploaded tech spec lands after creation. The user reviews the
 * document section by section against the original and approves the kept sections as the
 * product's spec. The section review owns every state (loading, still reading, failed,
 * nothing to read, already reviewed).
 */

const paramsSchema = z.object({
  productId: z.coerce.number().int().positive(),
});

export const Route = createFileRoute('/product-account_/$productId_/review')({
  params: {
    // Opaque token in the address bar, never the global product id; a legacy
    // numeric link still resolves.
    parse: ({ productId }) =>
      paramsSchema.parse({ productId: decodeProductId(productId) ?? productId }),
    stringify: ({ productId }) => ({ productId: encodeProductId(productId) }),
  },
  component: ReviewPage,
});

export function ReviewPage() {
  const { productId } = Route.useParams();
  // The section review owns its hero + main; the supporting documents follow in a main of
  // their own so they stay in view while the spec is reviewed.
  return (
    <>
      <SectionReviewScreen productId={productId} />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        <SupportingDocsSection productId={productId} />
      </main>
    </>
  );
}

/** Supporting docs sit right below the review as a "no review" pile, so it's clear
 *  only the tech spec is reviewed. Same self-contained uploader as the edit page. */
function SupportingDocsSection({ productId }: { productId: number }) {
  return (
    <section className="mt-10">
      {/* No intro line: the heading says "No review" and the uploader carries its own
          description, so a second one only repeated them. */}
      <h2 className="text-navy text-[18px] font-bold m-0 mb-4">
        Supporting documents{' '}
        <span className="text-muted font-medium text-[13px]">&middot; No review</span>
      </h2>
      <div className="rounded-2xl border border-line bg-white shadow-card p-6">
        <SupportingDocumentsUploader productId={productId} />
      </div>
    </section>
  );
}
